import { Link, useNavigate } from 'react-router-dom';
import { Home, Building2, UserCircle2, LogOut, Key, Settings, BarChart3, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';

export const Navigation = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    onLogout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <nav data-testid="main-navigation" className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="px-4 md:px-12 lg:px-24 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <Building2 className="w-6 h-6 md:w-8 md:h-8 text-primary" />
          <span className="text-lg md:text-2xl font-serif font-bold tracking-tight">ARYA</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/" data-testid="nav-home" className="text-sm uppercase tracking-widest hover:text-primary transition-colors duration-300">
            Accueil
          </Link>
          <Link to="/properties" data-testid="nav-properties" className="text-sm uppercase tracking-widest hover:text-primary transition-colors duration-300">
            Biens
          </Link>
          <Link to="/rental-management" data-testid="nav-rental" className="text-sm uppercase tracking-widest hover:text-primary transition-colors duration-300">
            Gestion Locative
          </Link>

          {user ? (
            <div className="flex items-center gap-4">
              <Link 
                to={user.role === 'admin' ? '/admin' : '/dashboard'} 
                data-testid="nav-dashboard"
                className="flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary transition-colors duration-300"
              >
                <UserCircle2 className="w-4 h-4" />
                {user.role === 'admin' ? 'Biens' : 'Mon Espace'}
              </Link>
              {user.role === 'admin' && (
                <>
                  <Link 
                    to="/admin/rentals" 
                    data-testid="nav-admin-rentals"
                    className="flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary transition-colors duration-300"
                  >
                    <Key className="w-4 h-4" />
                    Locations
                  </Link>
                  <Link 
                    to="/admin/stats" 
                    data-testid="nav-admin-stats"
                    className="flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary transition-colors duration-300"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Stats
                  </Link>
                  <Link 
                    to="/admin/settings" 
                    data-testid="nav-admin-settings"
                    className="flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary transition-colors duration-300"
                  >
                    <Settings className="w-4 h-4" />
                    Paramètres
                  </Link>
                </>
              )}
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-sm uppercase tracking-widest"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-5 text-xs uppercase tracking-widest font-semibold transition-all duration-300">
                Connexion
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-t border-border">
          <div className="px-4 py-4 space-y-4">
            <Link to="/" onClick={closeMenu} className="block text-sm uppercase tracking-widest hover:text-primary py-2">
              Accueil
            </Link>
            <Link to="/properties" onClick={closeMenu} className="block text-sm uppercase tracking-widest hover:text-primary py-2">
              Biens
            </Link>
            <Link to="/rental-management" onClick={closeMenu} className="block text-sm uppercase tracking-widest hover:text-primary py-2">
              Gestion Locative
            </Link>

            {user ? (
              <>
                <Link 
                  to={user.role === 'admin' ? '/admin' : '/dashboard'} 
                  onClick={closeMenu}
                  className="block text-sm uppercase tracking-widest hover:text-primary py-2"
                >
                  {user.role === 'admin' ? 'Biens (Admin)' : 'Mon Espace'}
                </Link>
                {user.role === 'admin' && (
                  <>
                    <Link to="/admin/rentals" onClick={closeMenu} className="block text-sm uppercase tracking-widest hover:text-primary py-2">
                      Locations
                    </Link>
                    <Link to="/admin/stats" onClick={closeMenu} className="block text-sm uppercase tracking-widest hover:text-primary py-2">
                      Stats
                    </Link>
                    <Link to="/admin/settings" onClick={closeMenu} className="block text-sm uppercase tracking-widest hover:text-primary py-2">
                      Paramètres
                    </Link>
                  </>
                )}
                <button
                  onClick={handleLogout}
                  className="block text-sm uppercase tracking-widest hover:text-primary py-2 text-left w-full"
                >
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/login" onClick={closeMenu}>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 py-3 text-xs uppercase tracking-widest font-semibold">
                  Connexion
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};